import { AssetsForms } from '@/entities/assets';
import { ContactsForms } from '@/entities/contact';
import { isPrivateDocumentCategory } from '@/entities/document';
import { getPersonalIdentifierForms } from '@/entities/personalIdentifiers/helpers/get-forms';
import { AiAsset, ApiContactDto, ApiExtractItem, ApiPrivateDocumentDto } from '@/shared/api';
import { PrivateDocumentForm } from '@/shared/lib/forms/documents/private';
import { AssetDto, isAssetCategory } from '@/shared/lib/sj-orm/models/asset.dto';
import { ContactDto, isContactType } from '@/shared/lib/sj-orm/models/contact.dto';
import {
  isPersonalIdentifierType,
  PersonalIdentifierDocumentDto,
} from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { PrivateDocumentDto } from '@/shared/lib/sj-orm/models/document/private.document.dto';

import { NonNullableFields } from '../types';

const sanitizeInput = <T extends object>(
  data: unknown,
  allowedKeys: string[],
): NonNullableFields<T> | null => {
  if (!data || typeof data !== 'object') return null;

  const filtered = Object.entries(data).filter(([key, value]) => {
    const isValid = allowedKeys.includes(key);
    if (!isValid) console.warn(`Unknown property - ${key}`);
    return isValid && value !== null;
  });

  return Object.fromEntries(filtered) as NonNullableFields<T>;
};

const extractKeys = (form: unknown) => {
  if (!form) return [];
  return [
    ...new Set((Array.isArray(form) ? form : [form]).flatMap((fields) => Object.keys(fields))),
  ];
};

type AssetProps = {
  data: AiAsset;
  type?: string | null;
};

export const getValidAsset = ({ data, type }: AssetProps) => {
  if (!data || !type || !data.country || !data.purchaseValue || !isAssetCategory(type)) {
    return null;
  }

  const allowedKeys = ['category', ...extractKeys(AssetsForms[type])];

  return sanitizeInput<Omit<AssetDto, 'id'>>(data, allowedKeys);
};

type PersonalIdProps = {
  data: ApiExtractItem['data'];
  type?: string | null;
};

export const getValidPersonalId = ({ data, type }: PersonalIdProps) => {
  if (!data || !type || !isPersonalIdentifierType(type)) {
    return null;
  }

  const form = getPersonalIdentifierForms(type);
  const allowedKeys = extractKeys(form);

  return sanitizeInput<Omit<PersonalIdentifierDocumentDto, 'id'>>(data, allowedKeys);
};

type PrivateDocumentProps = {
  data: ApiPrivateDocumentDto;
  privateCategory?: string | null;
};

export const getValidPrivateDocument = ({ data, privateCategory }: PrivateDocumentProps) => {
  if (!data || !privateCategory || !isPrivateDocumentCategory(privateCategory)) {
    return null;
  }
  const allowedKeys = [...extractKeys([PrivateDocumentForm]), 'privateCategory'];

  return sanitizeInput<Omit<PrivateDocumentDto, 'id'>>(data, allowedKeys);
};

type ContactProps = {
  data: ApiContactDto;
  type?: string | null;
};

export const getValidContact = ({ data, type }: ContactProps) => {
  if (!data || !type || !data.selectedCountry || !isContactType(type)) {
    return null;
  }
  const allowedKeys = extractKeys(ContactsForms[type]);

  return sanitizeInput<Omit<ContactDto, 'id'>>(data, allowedKeys);
};
